import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import '../../styles/FilePreviewModal.css';

// Renamed component to better describe its purpose
const DocumentPreviewViewer = ({ isOpen, onClose, attachment, fileTypes }) => {
  const [fileType, setFileType] = useState('loading');
  const [fileData, setFileData] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
console.log(attachment)
  // Process attachment and fileTypes when the modal opens or attachment changes
  useEffect(() => {
    if (isOpen && (attachment || fileTypes)) {
      setFileType('loading');
      setErrorMessage('');
      processAttachment();
    }
  }, [isOpen, attachment, fileTypes]);

  const getFileExtension = (filename) => {
    if (!filename) return '';
    // Convert to lowercase and get the part after the last dot
    return filename.toLowerCase().split('.').pop();
  };

  // Optimized helper function to handle base64 data properly
  const processBase64Data = (data) => {
    if (!data) return '';
    
    // If it's already a data URL, return it as is
    if (typeof data === 'string' && data.startsWith('data:')) {
      return data;
    }
    
    // Clean up the base64 string and remove any data URL prefix if present
    return typeof data === 'string' 
      ? data.replace(/\s+/g, '').replace(/^data:[^;]+;base64,/, '') 
      : data;
  };

  const processAttachment = () => {
    try {
      // Case 1: fileTypes contains filename and attachment contains base64 data
      if (fileTypes && attachment) {
        const fileName = typeof fileTypes === 'string' ? fileTypes : 
                       (Array.isArray(fileTypes) && fileTypes.length > 0) ? fileTypes[0] : '';
        
        if (!fileName) {
          setErrorMessage('Filename is missing');
          setFileType('error');
          return;
        }

        handleFileTypeByExtension(
          getFileExtension(fileName), 
          processBase64Data(attachment), 
          fileName
        );
        return;
      }
      
      // Case 2: No attachment provided
      if (!attachment) {
        setErrorMessage('Attachment data is missing');
        setFileType('error');
        return;
      }

      // Case 3: String attachment (filename only)
      if (typeof attachment === 'string') {
        handleFileTypeByExtension(getFileExtension(attachment), null, attachment);
        return;
      }

      // Case 4: Object attachment with src property
      if (attachment.src) {
        handleFileTypeByExtension(
          getFileExtension(attachment.name || ''), 
          processBase64Data(attachment.src), 
          attachment.name || ''
        );
        return;
      }
      
      // Case 5: Unhandled format
      setErrorMessage('Unable to process attachment: invalid format');
      setFileType('error');
    } catch (error) {
      console.error('Error processing attachment for preview:', error);
      setErrorMessage('Failed to prepare file for preview: ' + error.message);
      setFileType('error');
    }
  };

  const handleFileTypeByExtension = (extension, base64Data, fileName) => {
    // If base64Data already includes the data URL prefix, use it carefully
    if (typeof base64Data === 'string' && base64Data.startsWith('data:')) {
      const mimeType = (base64Data.split(';')[0].split(':')[1] || '').toLowerCase();

      // Block HTML / SVG / script MIME types from rendering
      if (
        mimeType.includes('html') ||
        mimeType.includes('svg') ||
        mimeType.includes('javascript') ||
        mimeType.includes('xml')
      ) {
        setFileType('unsupported');
        setFileData('');
        setErrorMessage('This file type cannot be previewed for security reasons.');
        return;
      }

      if (mimeType.includes('pdf')) {
        setFileData(base64Data);
        setFileType('pdf');
        setErrorMessage('');
        return;
      }

      if (
        mimeType === 'image/jpeg' ||
        mimeType === 'image/jpg' ||
        mimeType === 'image/png' ||
        mimeType === 'image/gif'
      ) {
        setFileData(base64Data);
        setFileType('image');
        setErrorMessage('');
        return;
      }

      setFileData(base64Data);
      setFileType('unsupported');
      setErrorMessage('This file type cannot be previewed directly. Please download the file.');
      return;
    }
    
    // Handle based on file extension
    let dataUrl = '';
    
    switch (extension) {
      case 'pdf':
        setFileType('pdf');
        dataUrl = base64Data ? `data:application/pdf;base64,${base64Data}` : '';
        setErrorMessage('');
        break;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        setFileType('image');
        dataUrl = base64Data ? `data:image/${extension === 'jpg' ? 'jpeg' : extension};base64,${base64Data}` : '';
        setErrorMessage('');
        break;
      case 'doc':
      case 'docx':
      case 'xls':
      case 'xlsx':
        setFileType('unsupported');
        dataUrl = base64Data ? `data:application/octet-stream;base64,${base64Data}` : '';
        setErrorMessage(`${extension.toUpperCase()} files cannot be previewed directly. Please download the file.`);
        break;
      default:
        setFileType('unsupported');
        dataUrl = base64Data
          ? `data:application/octet-stream;base64,${base64Data}`
          : '';
        setErrorMessage('This file type cannot be previewed directly. Please download the file.');
    }

    setFileData(dataUrl);
  };

  const handleDownload = () => {
    if (!fileData) {
      setErrorMessage('No file data available for download');
      return;
    }
    
    try {
      const link = document.createElement('a');
      link.href = fileData;
      link.download = getFileName();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading file:', error);
      setErrorMessage('Failed to download file: ' + error.message);
    }
  };

  // Helper function to get filename for display
  const getFileName = () => {
    return (
      (typeof fileTypes === 'string' && fileTypes) ||
      (Array.isArray(fileTypes) && fileTypes.length > 0 && fileTypes[0]) ||
      (typeof attachment === 'string' && attachment) ||
      (attachment && attachment.name) ||
      'File Preview'
    );
  };

  if (!isOpen) return null;

  return (
    <div className="file-preview-modal">
      <div className="file-preview-backdrop" onClick={onClose}></div>
      <div className="file-preview-container">
        <div className="file-preview-header">
          <h3>
            <i className="bi bi-file-earmark"></i>
            {getFileName()}
          </h3>
          <div className="file-preview-actions">
            {fileData && (
              <button className="preview-action-btn" onClick={handleDownload} title="Download">
                <i className="bi bi-download"></i>
              </button>
            )}
            <button className="preview-action-btn" onClick={onClose} title="Close">
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <div className="file-preview-content">
          {fileType === 'loading' && (
            <div className="file-preview-loading">
              <i className="bi bi-arrow-repeat spinning"></i>
              <p>Loading preview...</p>
            </div>
          )}

          {fileType === 'error' && (
            <div className="file-preview-error">
              <i className="bi bi-exclamation-triangle"></i>
              <p>{errorMessage || 'Error loading file preview'}</p>
              <button className="button secondary" onClick={onClose}>Close</button>
            </div>
          )}

          {fileType === 'unsupported' && (
            <div className="file-preview-unsupported">
              <i className="bi bi-file-earmark-x"></i>
              <p>{errorMessage}</p>
              {fileData && (
                <button className="button primary" onClick={handleDownload}>
                  <i className="bi bi-download"></i> Download File
                </button>
              )}
            </div>
          )}

          {fileType === 'pdf' && fileData && fileData.startsWith('data:application/pdf') && (
            <iframe 
              src={fileData} 
              className="pdf-preview" 
              title={getFileName()}
              width="100%" 
              height="100%"
            ></iframe>
          )}

          {fileType === 'image' && fileData && fileData.startsWith('data:image/') && !fileData.startsWith('data:image/svg') && (
            <div className="image-preview-wrapper">
              <img 
                src={fileData} 
                alt={getFileName()} 
                className="image-preview"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

DocumentPreviewViewer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  attachment: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      name: PropTypes.string,
      src: PropTypes.string,
      desc: PropTypes.string,
      uploadDate: PropTypes.string
    })
  ]),
  fileTypes: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array
  ])
};

export default DocumentPreviewViewer;